import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import {
    HasProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BadgeImagePlacementValues, BadgeModel, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import Badge from "@insite/mobius/Badge";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import customUseOverlayBadge from "@tSurya/Common/Hooks/customUseOverlayBadge";
import ProductDescriptionplp from "@tSurya/Components/SURProductDescriptionplp";
import NextArrow from "@tSurya/Icons/NextArrow";
import PreviousArrow from "@tSurya/Icons/PreviousArrow";
import ProductAddToListLink from "@tSurya/Overrides/Components/ProductAddToListLink";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel;
    extendedStyles?: ProductImageStyles;
    showTextBadge?: boolean;
    showImageBadge?: boolean;
    maximumNumberOfTextBadges?: number;
    showAddToList?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    view: state.pages.productList.view,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductImageStyles {
    linkWrappingImage?: ClickablePresentationProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    container?: InjectableCss;
    productDetailLink?: LinkPresentationProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandItem?: InjectableCss;
    descriptionItem?: InjectableCss;
    topSeller?: InjectableCss;
    updates?: InjectableCss;
    new?: InjectableCss;
    brandDescriptionInfo?: InjectableCss;
    brandDescriptionInfoExtraMargin?: InjectableCss;
    list?: InjectableCss;
    wrapper?: InjectableCss;
    SwatchImages?: InjectableCss;
    ChiledImages?: InjectableCss;
    swatchesDiv?: InjectableCss;
    carousalContainer?: InjectableCss;
    carousalWrapper?: InjectableCss;
    carousalCTA?: ButtonPresentationProps;
    addtolistStyles?: InjectableCss;
    badgeContainer?: InjectableCss;
    mainImageWrapper?: InjectableCss;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageCss?: JSX.IntrinsicElements["img"];
}

export const productImageStyles: ProductImageStyles = {
    wrapper: {
        css: css``,
    },
    mainImageWrapper: {
        css: css`
            position: relative;
        `,
    },
    linkWrappingImage: {
        css: css`
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 95%;
                        `,
                        css`
                            width: 95%;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    topSeller: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            font-weight: ${TYPOGRAPHY.weight.bold};
            /* color: ${getColor("text.accent")}; */
            color: ${getColor("text.accent")};
            /* color: ${getColor("danger")}; */
            padding-right: 8px;
        `,
    },
    updates: {
        css: css`
            display: flex;
        `,
    },
    new: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            font-weight: ${TYPOGRAPHY.weight.bold};
            /* color: ${getColor("text.accent")}; */
            color: ${getColor("text.accent")};
            /* color: ${getColor("warning")}; */
        `,
    },
    image: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    img: {
        css: css`
            height: auto;
            width: 100%;
        `,
    },
    container: {
        css: css`
            min-width: 100%;
        `,
    },
    brandStyles: {
        nameLink: {
            css: css`
                margin-right: 8px;
                pointer-events: none;
                span {
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    font-size: ${TYPOGRAPHY.size.base};
                    line-height: ${TYPOGRAPHY.lineHeight.xbase};
                    color: ${getColor("text.main")} !important;
                }
            `,
        },
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.base};
                font-size: ${TYPOGRAPHY.size.base};
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
                > span {
                    color: ${getColor("text.main")} !important;
                    font-weight: ${TYPOGRAPHY.weight.base} !important;
                }
            `,
            typographyProps: {
                css: css`
                    color: ${getColor("text.main")} !important;
                `,
            },
        },
    },
    brandDescriptionInfo: {
        css: css`
            margin-top: 5px;
            margin-bottom: 5px;
            margin-right: 8px;
            display: flex;
            flex-wrap: wrap;
            div {
                > a {
                    width: auto;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    width: auto;
                                `,
                                css`
                                    width: auto;
                                `,
                                null,
                                null,
                                null,
                            ],
                            "max",
                        )}
                }
            }
        `,
    },
    SwatchImages: {
        css: css`
            width: 50px;
            height: 50px;
            &:hover {
                border: 1px solid ${getColor("common.border")} !important;
                /* padding: 4px 5px; */
            }
            img {
                padding-left: 0px !important;
                padding-right: 0px !important;
                padding-top: 0 !important;
                padding-bottom: 0 !important;
                width: 100%;
                height: 100%;
                background: rgb(248, 248, 248);
                object-fit: contain;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 35px;
                        max-height: 35px;
                    `,
                    css`
                        max-width: 35px;
                        max-height: 35px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    ChiledImages: {
        css: css`
            //flex-basis: 50%;
        `,
    },
    swatchesDiv: {
        css: css`
            display: flex;
            column-gap: 6px;
            flex-wrap: nowrap;
            row-gap: 10px;
            > div > a > span {
                margin-bottom: 0px !important;
            }
        `,
    },
    carousalContainer: {
        css: css`
            display: flex;
            padding-top: 5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0px 6px;
                        padding-top: 5px;
                    `,
                    css`
                        padding: 0px 6px;
                        padding-top: 5px;
                    `,
                    css`
                        padding: 0px 6px;
                        padding-top: 5px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    carousalWrapper: {
        css: css`
            flex-grow: 2;
            overflow: hidden;
            margin: 0 5px;
            width: 95%;
        `,
    },
    carousalCTA: {
        css: css`
            background: ${getColor("primary.contrast")};
            flex: 1;
            width: 50px;
            padding: 0;
            border: 0;
            :hover {
                background-color: #fff;
                width: 50px;
            }
            svg {
                width: 20px;
            }
        `,
    },
    addtolistStyles: {
        css: css`
            position: absolute;
            /* bottom: 1%;
            right: 3%; */
            right: 12px;
            bottom: 13px;
            span {
                width: 16px;
                height: 16px;
                cursor: pointer;

                svg {
                    width: 16px;
                    height: 16px;
                    cursor: pointer;
                }

                &::after {
                    content: "Add to Project";
                    position: absolute;
                    bottom: 125%;
                    left: 30%;
                    transform: translateX(-50%) translateX(-40px);
                    background-color: #333;
                    color: #fff;
                    text-align: center;
                    padding: 5px 10px;
                    border-radius: 4px;
                    white-space: nowrap;
                    font-size: 12px;
                    z-index: 10000;
                    transition: opacity 0.3s;
                    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
                    opacity: 0;
                    visibility: hidden;
                }

                &:hover::after {
                    visibility: visible;
                    opacity: 1;
                }

                &.hide-tooltip::after {
                    visibility: hidden !important;
                    opacity: 0 !important;
                }
            }
            /* Media query for mobile */
            @media (max-width: 768px),
                (orientation: portrait) and (max-width: 1024px),
                (max-width: 1024px) and (min-width: 768px) {
                span::after {
                    display: none;
                }
            }
        `,
    },
    badgeContainer: {
        css: css`
            display: flex;
            gap: 8px;
            margin-top: 12px;
            flex-wrap: wrap;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 75%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            font-style: normal;
            font-weight: 700;
            font-size: 13px;
            line-height: 22px;
            white-space: nowrap;
            ${wrapInContainerStyles}
        `,
    },
    badgeImageCss: {
        style: {
            maxWidth: "auto",
            maxHeight: "24px",
            objectFit: "contain",
        },
    },
};

const SurProductImage: FC<Props> = ({
    product,
    productContext,
    extendedStyles,
    view,
    showTextBadge,
    showImageBadge,
    maximumNumberOfTextBadges,
    showAddToList,
}) => {
    const [styles] = React.useState(() => mergeToNew(productImageStyles, extendedStyles));

    const { showOverlayBadge, overlayBadge, overlayBadgeWrapperStyles } = customUseOverlayBadge({
        productContext,
        showTextBadge,
        showImageBadge,
        styles,
    });

    let altText = "product" in product ? product.product.imageAltText : product.altText;
    if (!altText || altText === "") {
        altText = "product" in product ? product.product.productTitle : product.productName;
    }

    const properties = "product" in product ? product.product.properties : product.properties;

    const masterData = properties?.masterDesignRelatedProducts
        ? JSON.parse(properties?.masterDesignRelatedProducts)
        : undefined;

    const [image, setImage] = React.useState(false);
    const imagePath = "product" in product ? product.product.smallImagePath : product.smallImagePath;

    const [swatchImage, setSwatchImage] = React.useState(imagePath);

    const hoverImage = "product" in product ? product.product.properties?.roomSceneImagePath : product.smallImagePath;

    const productDetailPath =
        "product" in product
            ? product.product.id.includes("Temp")
                ? product.product.urlSegment
                : product.productInfo.productDetailPath
            : product.productUri;
    const loading = product.idx === undefined ? "lazy" : product.idx < 3 ? "eager" : "lazy";

    const productBrand = "product" in product ? product.product.brand : product.brand;
    const productInfo = "product" in product && product.product.badges;
    const showTittle = "product" in product ? product.product.productTitle : product.productName;

    const hoverFunction = () => {
        if (hoverImage !== undefined) {
            setImage(true);
        }
    };

    const hoverFunctionSwatch = (MediumImagePath: string) => {
        setSwatchImage(MediumImagePath);
    };

    const removeHoverFunctionSwatch = () => {
        setSwatchImage(imagePath);
    };

    const imageSrc = image ? hoverImage : swatchImage;

    function isMobile() {
        const matchMediaFn =
            typeof window !== "undefined" && window.matchMedia ? window.matchMedia : () => ({ matches: false });
        const match = matchMediaFn("(max-width: 768px)");
        return match.matches;
    }

    const [currentSlide, setCurrentSlide] = React.useState(0);
    const totalSlides = masterData?.length;
    const smallScreen = isMobile() || view === "Grid";
    const slidesToShow = smallScreen ? 4 : 5;
    const slidesToScroll = 1;
    const totalVisibleSlides = Math.min(slidesToShow, totalSlides);
    const canScrollPrev = currentSlide > 0;
    const canScrollNext =
        totalSlides > slidesToShow && smallScreen
            ? currentSlide + totalVisibleSlides < totalSlides + 1
            : currentSlide + totalVisibleSlides < totalSlides;

    const handlePrevClick = () => {
        setCurrentSlide(Math.max(currentSlide - slidesToScroll, 0));
    };

    const handleNextClick = () => {
        setCurrentSlide(Math.min(currentSlide + slidesToScroll, totalSlides + 1 - totalVisibleSlides));
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.mainImageWrapper}>
                <Clickable {...styles.linkWrappingImage} href={productDetailPath} data-test-selector="productImage">
                    <Img
                        {...styles.img}
                        loading={loading}
                        width="209"
                        height="209"
                        src={imageSrc}
                        altText={altText}
                        onMouseOver={() => {
                            hoverFunction();
                        }}
                        onMouseOut={() => {
                            setImage(false);
                        }}
                    />
                </Clickable>
                {showAddToList === true ? (
                    <StyledWrapper {...styles.addtolistStyles}>
                        <ProductAddToListLink showIcon={true}></ProductAddToListLink>
                    </StyledWrapper>
                ) : null}
            </StyledWrapper>
            {(showTextBadge || showImageBadge) && (
                <StyledWrapper {...styles.badgeContainer}>
                    {productInfo &&
                        productInfo?.slice(0, maximumNumberOfTextBadges).map(badge => {
                            return (
                                // eslint-disable-next-line react/jsx-key
                                <Badge
                                    type={badge.badgeType}
                                    value={badge.badgeType === "Image" ? badge.largeImageBadgePath : badge.displayText}
                                    badgeTextColor={badge.textColorHexCode}
                                    badgeBackgroundColor={badge.badgeColorHexCode}
                                    badgeTextRounding={badge.badgeStyle === "Round" ? 20 : 0}
                                    imageAltText={badge.imageAltText ? badge.imageAltText : badge.name}
                                    textBadgeCss={styles.badgeText}
                                    imageBadgeProps={{ ...styles.badgeImageCss }}
                                />
                            );
                        })}
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.brandDescriptionInfo}>
                {productBrand && (
                    <StyledWrapper {...styles.brandItem}>
                        <ProductBrand brand={productBrand} extendedStyles={styles.brandStyles} />
                    </StyledWrapper>
                )}
                {showTittle && (
                    <StyledWrapper {...styles.descriptionItem}>
                        <ProductDescriptionplp product={product} extendedStyles={styles.descriptionStyles} />
                    </StyledWrapper>
                )}
            </StyledWrapper>

            {totalSlides > slidesToShow ? (
                <StyledWrapper {...styles.carousalContainer}>
                    {canScrollPrev && (
                        <Button
                            disabled={!canScrollPrev}
                            onClick={handlePrevClick}
                            role="button"
                            aria-label="Previous Arrow"
                            {...styles.carousalCTA}
                        >
                            <PreviousArrow />
                        </Button>
                    )}
                    <StyledWrapper {...styles.carousalWrapper}>
                        <StyledWrapper
                            {...styles.swatchesDiv}
                            style={{
                                transform: `translateX(-${currentSlide * (100 / totalVisibleSlides - 1)}%)`,
                                columnGap: totalSlides > 10 ? "3px" : "6px",
                            }}
                        >
                            {masterData?.map(
                                (
                                    data: {
                                        [x: string]: string | undefined;
                                        SmallImagePath: string;
                                        MediumImagePath: string;
                                    },
                                    index: React.Key | undefined,
                                ) => {
                                    return (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <StyledWrapper
                                            key={index}
                                            {...styles.ChiledImages}
                                            style={{ flexBasis: `${100 / slidesToShow}%` }}
                                        >
                                            <Link href={`/Product/${data.UrlSegment}`} aria-label="Product Link">
                                                <LazyImage
                                                    src={data.SmallImagePath}
                                                    {...styles.SwatchImages}
                                                    onMouseOver={() => {
                                                        hoverFunctionSwatch(data.MediumImagePath);
                                                    }}
                                                    onMouseOut={() => {
                                                        removeHoverFunctionSwatch();
                                                    }}
                                                    altText="Product Image"
                                                />
                                            </Link>
                                        </StyledWrapper>
                                    );
                                },
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    {canScrollNext && (
                        <Button
                            disabled={!canScrollNext}
                            onClick={handleNextClick}
                            role="button"
                            aria-label="Next Arrow"
                            {...styles.carousalCTA}
                        >
                            <NextArrow />
                        </Button>
                    )}
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.swatchesDiv}>
                    {masterData?.map(
                        (
                            data: {
                                [x: string]: string | undefined;
                                SmallImagePath: string;
                                MediumImagePath: string;
                            },
                            index: React.Key | undefined,
                        ) => {
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <StyledWrapper key={index} {...styles.ChiledImages}>
                                    <Link href={`/Product/${data.UrlSegment}`} aria-label="Product Link">
                                        <LazyImage
                                            src={data.SmallImagePath}
                                            {...styles.SwatchImages}
                                            onMouseOver={() => {
                                                hoverFunctionSwatch(data.MediumImagePath);
                                            }}
                                            onMouseOut={() => {
                                                removeHoverFunctionSwatch();
                                            }}
                                            altText="Product Image"
                                        />
                                    </Link>
                                </StyledWrapper>
                            );
                        },
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps)(SurProductImage));
